"use client";

import React from "react";

import Tags from "../Tags";
import Icon from "@/components/icons";
import { Button } from "@/components/ui/button";
import { formatPrice } from "@/utils/functions/formatting";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { DEFAULT_USER_CURRENCY } from "@/lib/course/utils/constants";
import { type ProgramCard } from "@/types/ProgramCard";
import { AttributeProps } from "./types";
import { PACING_TEXT } from "./constant";
import useTranslations from "@/i18n/useTranslations";

export function Content({
  name,
  type,
  ctaText,
  totalPrice,
  pacing = "",
  alignment = "column",
  priceWithoutDiscount,
  childCoursesCount = 0,
  hasShadow = true,
}: ProgramCard) {
  const t = useTranslations("ProductCompare");
  const priceStyles = priceWithoutDiscount ? "justify-evenly" : "justify-start";

  const alignmentStyles = {
    row: {
      content: "flex-row",
      attributes: "flex-row gap-4",
      pricingContainer: "flex-row items-center",
      price: "ml-auto",
      button: "w-auto",
    },
    column: {
      content: "flex-col justify-between",
      attributes: "flex-col gap-2.5",
      pricingContainer: `flex-col items-end ${priceStyles}`,
      price: "",
      button: "w-full",
    },
  };

  const price = alignmentStyles[alignment].price;
  const button = alignmentStyles[alignment].button;
  const contentContainer = alignmentStyles[alignment].content;
  const attributesContainer = alignmentStyles[alignment].attributes;
  const pricingContainer = alignmentStyles[alignment].pricingContainer;

  return (
    <Card
      className={`bg-white w-full flex flex-col border-none ${hasShadow ? "shadow-hero-rail" : "!shadow-none"}`}
    >
      <CardHeader className="flex flex-col flex-1 rounded-t-xl p-4 bg-secondary-500 border-secondary-500 text-white font-bold text-3xl">
        {!!type && (
          <div className="mb-4">
            <Tags
              variant="badges"
              tags={[
                {
                  text: type,
                  icon: "certificate",
                  color: "lightFern",
                  borderless: true,
                },
              ]}
            />
          </div>
        )}
        <h4 className="text-inherit text-wrap line-clamp-3">{name}</h4>
      </CardHeader>

      <CardContent
        className={`flex items-center p-4 border rounded-b-xl gap-4 ${contentContainer}`}
      >
        <div className="flex justify-between gap-1 w-full">
          <div className={`flex flex-1 ${attributesContainer}`}>
            {!!childCoursesCount && (
              <Attribute
                icon="certificate"
                text={`${childCoursesCount} course${childCoursesCount !== 1 ? "s" : ""}`}
              />
            )}

            {!!pacing && (
              <Attribute
                icon="speedometer"
                text={PACING_TEXT[pacing as keyof typeof PACING_TEXT] ?? pacing}
              />
            )}
          </div>

          {!!totalPrice && (
            <div className={`flex gap-2.5 ${pricingContainer}`}>
              <strong className={`font-bold text-lg break-all ${price}`}>
                {formatPrice(totalPrice, DEFAULT_USER_CURRENCY).replace(
                  " USD",
                  "",
                )}
              </strong>
              <span className="font-bold break-all text-lg line-through text-[#8E8E8E]">
                {!!priceWithoutDiscount &&
                  formatPrice(
                    priceWithoutDiscount,
                    DEFAULT_USER_CURRENCY,
                  ).replace(" USD", "")}
              </span>
            </div>
          )}
        </div>
        <Button className={`text-md ${button}`}>
          {ctaText || t("learnMore")}
        </Button>
      </CardContent>
    </Card>
  );
}

const Attribute = ({ icon, text }: AttributeProps) => (
  <div className="flex items-center">
    <Icon type={icon} svgProps={{ width: 24, height: 24 }} />
    <span className="text-lg ml-2.5">{text}</span>
  </div>
);
